// @ts-nocheck

import { type StateType } from '../../states';
import {
  type HistoryState,
  type HistoryEntitiesState,
  type HistoryMatches,
  type HistoryBets,
  type HistoryOutcomes,
  type HistoryCategories,
  type HistoryTournaments,
} from '../../states/history';
import { type HistoryMatchState } from '../../states/history';
import { type HistoryBetState } from '../../states/history';
import { type HistoryOutcomeState } from '../../states/history';
import { type HistorySportState } from '../../states/history';
import { type HistorySports } from '../../states/history';
import { type HistoryCategoryState } from '../../states/history';
import { type HistoryTournamentState } from '../../states/history';
import { type BetslipFilter } from '../../types/history';
import { type BetslipType } from '../../types/history';
import { type Selector } from '../../types/selectors';
import { SportId } from '../../types/sports';
import {
  getBetIdFromProps,
  getCategoryIdFromProps,
  getMatchIdFromProps,
  getOutcomeIdFromProps,
  getSportIdFromProps,
  getTournamentIdFromProps,
} from '../_propsGetters_';

const emptyObj = {};

export const selectHistoryState: Selector<HistoryState> = (state: StateType) => state.history;

//#region state.history
export const selectHistoryOffset: Selector<number> = (state: StateType) => selectHistoryState(state).offset;

export const selectHistoryLimit: Selector<number> = (state: StateType) => selectHistoryState(state).limit;

export const selectHistoryFilter: Selector<BetslipFilter | null> = (state: StateType) =>
  selectHistoryState(state).filter ?? null;

export const selectHistoryType: Selector<BetslipType | null> = (state: StateType) =>
  selectHistoryState(state).type ?? null;

export const selectHistoryStartDate: Selector<number | null> = (state: StateType) =>
  selectHistoryState(state).startDate ?? null;

export const selectHistoryEndDate: Selector<number | null> = (state: StateType) =>
  selectHistoryState(state).endDate ?? null;

export const selectHistoryNbBetslips: Selector<number> = (state: StateType) => selectHistoryState(state).nbBetslips;

export const selectHistoryLoading: Selector<boolean> = (state: StateType) => selectHistoryState(state).loading;

export const selectHistoryEntities: Selector<HistoryEntitiesState> = (state: StateType) =>
  selectHistoryState(state).entities;
//#endregion

//#region state.history.entities
export const selectHistoryEntitiesMatches: Selector<HistoryMatches> = (state: StateType) =>
  selectHistoryEntities(state)?.matches ?? emptyObj;

export const selectHistoryEntitiesBets: Selector<HistoryBets> = (state: StateType) =>
  selectHistoryEntities(state)?.bets ?? emptyObj;

export const selectHistoryEntitiesOutcomes: Selector<HistoryOutcomes> = (state: StateType) =>
  selectHistoryEntities(state)?.outcomes ?? emptyObj;

export const selectHistoryEntitiesSports: Selector<HistorySports> = (state: StateType) =>
  selectHistoryEntities(state)?.sports ?? emptyObj;

export const selectHistoryEntitiesCategories: Selector<HistoryCategories> = (state: StateType) =>
  selectHistoryEntities(state)?.categories ?? emptyObj;

export const selectHistoryEntitiesTournaments: Selector<HistoryTournaments> = (state: StateType) =>
  selectHistoryEntities(state)?.tournaments ?? emptyObj;
//#endregion

//#region state.history.entities.matches
type HistoryEntitiesMatchesEntitySelectorProps = {
  matchId?: number;
};

export const selectHistoryEntitiesMatchesEntity: Selector<HistoryMatchState | undefined> = (
  state: StateType,
  props: HistoryEntitiesMatchesEntitySelectorProps = {},
) => {
  const matchId = getMatchIdFromProps(state, props);
  const matches = selectHistoryEntitiesMatches(state);

  if (matchId) {
    return matches[matchId];
  }
};
//#endregion

//#region state.history.entities.bets
type HistoryEntitiesBetsEntitySelectorProps = {
  betId?: number;
};

export const selectHistoryEntitiesBetsEntity: Selector<HistoryBetState | undefined> = (
  state: StateType,
  props: HistoryEntitiesBetsEntitySelectorProps = {},
) => {
  const betId = getBetIdFromProps(state, props);
  const bets = selectHistoryEntitiesBets(state);

  if (betId) {
    return bets[betId];
  }
};
//#endregion

//#region state.history.entities.outcomes
export type HistoryEntitiesOutcomesEntitySelectorProps = {
  outcomeId?: number;
};

export const selectHistoryEntitiesOutcomesEntity: Selector<HistoryOutcomeState | undefined> = (
  state: StateType,
  props: HistoryEntitiesOutcomesEntitySelectorProps = {},
) => {
  const outcomeId = getOutcomeIdFromProps(state, props);
  const outcomes = selectHistoryEntitiesOutcomes(state);

  if (outcomeId) {
    return outcomes[outcomeId];
  }
};
//#endregion

//#region state.history.entities.sports
type HistoryEntitiesSportsEntitySelectorProps = {
  sportId?: number;
};

export const selectHistoryEntitiesSportsEntity: Selector<HistorySportState | undefined> = (
  state: StateType,
  props: HistoryEntitiesSportsEntitySelectorProps = {},
) => {
  const sportId = getSportIdFromProps(state, { sportId: props.sportId as SportId });
  const sports = selectHistoryEntitiesSports(state);

  if (sportId) {
    return sports[sportId];
  }
};
//#endregion

//#region state.history.entities.categories
type HistoryEntitiesCategoriesEntitySelectorProps = {
  categoryId?: number;
};

export const selectHistoryEntitiesCategoriesEntity: Selector<HistoryCategoryState | undefined> = (
  state: StateType,
  props: HistoryEntitiesCategoriesEntitySelectorProps = {},
) => {
  const categoryId = getCategoryIdFromProps(state, props);
  const categories = selectHistoryEntitiesCategories(state);

  if (categoryId) {
    return categories[categoryId];
  }
};
//#endregion

//#region state.history.entities.tournaments
type HistoryEntitiesTournamentsEntitySelectorProps = {
  tournamentId?: number;
};

export const selectHistoryEntitiesTournamentsEntity: Selector<HistoryTournamentState | undefined> = (
  state: StateType,
  props: HistoryEntitiesTournamentsEntitySelectorProps = {},
) => {
  const tournamentId = getTournamentIdFromProps(state, props);
  const tournaments = selectHistoryEntitiesTournaments(state);

  if (tournamentId) {
    return tournaments[tournamentId];
  }
};
//#endregion
